import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { fetchTenantSignInConfig, type TenantSignInConfig } from '@/api/iam';

const ORG_STORAGE_KEY = 'optimalhr.orgId';
const RECENT_STORAGE_KEY = 'optimalhr.recentWorkspaces';
const RECENT_MAX = 5;

export interface RecentWorkspace {
  slug: string;
  label: string;
  lastUsedAt: number;
}

function readRecent(): RecentWorkspace[] {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is RecentWorkspace =>
        x != null &&
        typeof x === 'object' &&
        typeof x.slug === 'string' &&
        typeof x.label === 'string' &&
        typeof x.lastUsedAt === 'number',
    );
  } catch {
    return [];
  }
}

function writeRecent(list: RecentWorkspace[]): void {
  try {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* localStorage may be disabled (private browsing, quota) — silently degrade. */
  }
}

// `optimal-it` → `Optimal-It`. Acronyms can't be detected reliably from a slug;
// this is best-effort. Future enhancement: derive label from the tenant's
// Organization name returned by sign-in-config.
function deriveLabel(slug: string): string {
  return slug
    .split('-')
    .map((part) => (part.length === 0 ? part : part[0]!.toUpperCase() + part.slice(1)))
    .join('-');
}

/**
 * Current organization (tenant). The orgId itself is persisted to
 * sessionStorage so a hard refresh keeps the user on the same tenant; the
 * matching sign-in config (Cognito pool details) is fetched fresh on boot
 * via `fetchTenantSignInConfig`.
 *
 * Recent workspaces (slug + display label only — never tokens) are kept
 * in localStorage so the /welcome screen can surface MRU chips across
 * sessions and devices that share the browser profile.
 *
 * Tokens are NEVER stored here. Amplify's own session manager owns those.
 */
export const useOrgStore = defineStore('org', () => {
  const orgId = ref<string | null>(sessionStorage.getItem(ORG_STORAGE_KEY));
  const signInConfig = ref<TenantSignInConfig | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const recentWorkspaces = ref<RecentWorkspace[]>(readRecent());

  const isReady = computed(() => Boolean(orgId.value && signInConfig.value));

  function persist(id: string | null) {
    if (id) sessionStorage.setItem(ORG_STORAGE_KEY, id);
    else sessionStorage.removeItem(ORG_STORAGE_KEY);
  }

  function recordRecent(slug: string) {
    const now = Date.now();
    const next: RecentWorkspace[] = [
      { slug, label: deriveLabel(slug), lastUsedAt: now },
      ...recentWorkspaces.value.filter((w) => w.slug !== slug),
    ].slice(0, RECENT_MAX);
    recentWorkspaces.value = next;
    writeRecent(next);
  }

  async function selectOrg(id: string) {
    // Fetch + Amplify-configure must complete before the credentials
    // form mounts. Setting orgId BEFORE the await would race: SignInPage
    // shows the credentials form on `orgId` truthy, and a user that
    // types fast could submit before configureAmplify runs. Surfaced by
    // Wave 3.1's E2E spec; the fix is to defer orgId.value until the
    // config is in hand.
    loading.value = true;
    error.value = null;
    try {
      const cfg = await fetchTenantSignInConfig(id);
      signInConfig.value = cfg;
      orgId.value = id;
      persist(id);
      recordRecent(id);
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load tenant config';
      signInConfig.value = null;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function reset() {
    orgId.value = null;
    signInConfig.value = null;
    error.value = null;
    persist(null);
  }

  return {
    orgId,
    signInConfig,
    loading,
    error,
    isReady,
    recentWorkspaces,
    selectOrg,
    reset,
  };
});
